import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export const cn = (...inputs: ClassValue[]) => {
  return twMerge(clsx(inputs));
};

interface CVDRiskData {
  sex: string;
  age: string;
  hdl_cholesterol: string;
  total_cholesterol: string;
  treats_hypertension: string;
  systolic_bp: string;
  smoker: string;
  diabetic: string;
}

const FRAMINGHAM_MEN = {
  lnAge: 52.00961,
  lnTotalChol: 20.014077,
  lnHdl: -0.905964,
  lnSbp: 1.305784,
  treatedBp: 0.241549,
  smoker: 12.096316,
  lnAgeLnTotalChol: -4.605038,
  lnAgeSmoker: -2.84367,
  lnAgeSq: -2.93323,
  constant: -172.300168,
  survivor: 0.9402,
} as const;

const FRAMINGHAM_WOMEN = {
  lnAge: 31.764001,
  lnTotalChol: 22.465206,
  lnHdl: -1.187731,
  lnSbp: 2.552905,
  treatedBp: 0.470051,
  smoker: 13.07543,
  lnAgeLnTotalChol: -5.06095,
  lnAgeSmoker: -2.996045,
  constant: -146.5933061,
  survivor: 0.98767,
} as const;

export interface CVDRiskResult {
  points: number;
  riskPercentage: number;
  valid: boolean;
}

export const calculateCVDRisk = (data: CVDRiskData): CVDRiskResult => {
  const age = parseFloat(data.age);
  const totalCholesterol = parseFloat(data.total_cholesterol);
  const hdl = parseFloat(data.hdl_cholesterol);
  const sbp = parseFloat(data.systolic_bp);
  const isMale = data.sex === "male";
  const treatedBp = data.treats_hypertension === "yes" ? 1 : 0;
  const smoker = data.smoker === "yes" ? 1 : 0;

  const hasSex = data.sex === "male" || data.sex === "female";
  const hasAge = Number.isFinite(age) && age >= 30 && age <= 79;
  const hasTotalChol = Number.isFinite(totalCholesterol) && totalCholesterol > 0;
  const hasHdl = Number.isFinite(hdl) && hdl > 0;
  const hasSbp = Number.isFinite(sbp) && sbp > 0;
  const hasTreated = data.treats_hypertension === "yes" || data.treats_hypertension === "no";
  const hasSmoker = data.smoker === "yes" || data.smoker === "no";

  if (!hasSex || !hasAge || !hasTotalChol || !hasHdl || !hasSbp || !hasTreated || !hasSmoker) {
    return { points: 0, riskPercentage: 0, valid: false };
  }

  const lnAge = Math.log(age);
  const lnTotalChol = Math.log(totalCholesterol);
  const lnHdl = Math.log(hdl);
  const lnSbp = Math.log(sbp);

  const ageForSmokerTerm = age > 70 ? Math.log(70) : lnAge;

  let L: number;

  if (isMale) {
    L =
      FRAMINGHAM_MEN.lnAge * lnAge +
      FRAMINGHAM_MEN.lnTotalChol * lnTotalChol +
      FRAMINGHAM_MEN.lnHdl * lnHdl +
      FRAMINGHAM_MEN.lnSbp * lnSbp +
      FRAMINGHAM_MEN.treatedBp * treatedBp +
      FRAMINGHAM_MEN.smoker * smoker +
      FRAMINGHAM_MEN.lnAgeLnTotalChol * lnAge * lnTotalChol +
      FRAMINGHAM_MEN.lnAgeSmoker * ageForSmokerTerm * smoker +
      FRAMINGHAM_MEN.lnAgeSq * lnAge * lnAge +
      FRAMINGHAM_MEN.constant;
  } else {
    L =
      FRAMINGHAM_WOMEN.lnAge * lnAge +
      FRAMINGHAM_WOMEN.lnTotalChol * lnTotalChol +
      FRAMINGHAM_WOMEN.lnHdl * lnHdl +
      FRAMINGHAM_WOMEN.lnSbp * lnSbp +
      FRAMINGHAM_WOMEN.treatedBp * treatedBp +
      FRAMINGHAM_WOMEN.smoker * smoker +
      FRAMINGHAM_WOMEN.lnAgeLnTotalChol * lnAge * lnTotalChol +
      FRAMINGHAM_WOMEN.lnAgeSmoker * ageForSmokerTerm * smoker +
      FRAMINGHAM_WOMEN.constant;
  }

  const survivor = isMale ? FRAMINGHAM_MEN.survivor : FRAMINGHAM_WOMEN.survivor;
  const P = 1 - Math.pow(survivor, Math.exp(L));
  const riskPercentage = Math.round(P * 1000) / 10;

  return {
    points: Math.round(L * 100) / 100,
    riskPercentage,
    valid: true,
  };
};

export const DOSE_OPTIONS = [
  "1 tablet",
  "1/2 tablet",
  "2 tablets",
  "1 capsule",
  "1/2 capsule",
  "2 capsules",
  "5 ml",
  "10 ml",
  "15 ml",
  "1 injection",
];

export const FREQUENCY_OPTIONS = [
  "1-0-1",
  "1-1-1",
  "0-0-1",
  "1-0-0",
  "0-1-0",
  "1-1-0",
  "0-1-1",
  "1-0-1",
  "Once daily",
  "Twice daily",
  "Thrice daily",
  "Every 4 hours",
  "Every 6 hours",
  "Every 8 hours",
  "Every 12 hours",
];

export const TIMING_OPTIONS = [
  "Before meal",
  "After meal",
  "With meal",
  "Early morning",
  "Morning",
  "Afternoon",
  "Evening",
  "Night",
  "Bedtime",
  "Empty stomach",
];

export const DURATION_OPTIONS = [
  "1 day",
  "3 days",
  "5 days",
  "7 days",
  "10 days",
  "14 days (2 weeks)",
  "21 days (3 weeks)",
  "30 days (1 month)",
  "60 days (2 months)",
  "90 days (3 months)",
  "120 days (4 months)",
  "150 days (5 months)",
  "180 days (6 months)",
  "210 days (7 months)",
  "240 days (8 months)",
  "270 days (9 months)",
  "300 days (10 months)",
  "330 days (11 months)",
  "360 days (12 months)",
];

export const START_FROM_OPTIONS = [
  "Day 1 (Immediate)",
  "Day 2",
  "Day 3",
  "Day 5",
  "After 7 days",
  "After 14 days",
  "After 30 days",
  "After 60 days",
  "After 90 days",
  "Alternate days",
  "Weekly",
];

export const COMMON_ADVICES = [
  {
    name: "Avoid soy, sugar, dairy, wheat products, maida, refined foods, deep fried foods, peanuts",
    notes: "Dietary restrictions for better health",
  },
  {
    name: "Include good fats like olive oil, coconut milk, MCT oil, nuts and seeds",
    notes: "Healthy fats recommendation",
  },
  {
    name: "Nitric oxide dump exercises",
    notes: "Exercise recommendation",
  },
  {
    name: "Stay hydrated with 8-10 glasses of water daily",
    notes: "Hydration advice",
  },
  {
    name: "Get 7-8 hours of quality sleep",
    notes: "Sleep recommendation",
  },
  {
    name: "Regular moderate exercise",
    notes: "Exercise recommendation",
  },
  {
    name: "Stress management techniques",
    notes: "Mental health advice",
  },
];

export const FOLLOW_UP_OPTIONS = [
  { days: 7, label: "7 Days" },
  { days: 14, label: "14 Days" },
  { days: 30, label: "30 Days" },
  { days: 90, label: "90 Days" },
];

// QRISK3-2017 Calculator
// Based on the official QRISK3 algorithm from https://qrisk.org
// Full pipeline: fractional polynomials, centering, linear predictor, survival transform
interface QRISKInputData {
  age: string;
  sex: string;
  ethnicity: string;
  bmi: string;
  height?: string;
  weight?: string;
  systolic_bp: string;
  systolic_bp_std_dev: string;
  cholesterol_hdl_ratio: string;
  smoking_status: string;
  diabetes_status: string;
  family_history_cvd: string;
  chronic_kidney_disease: string;
  atrial_fibrillation: string;
  on_blood_pressure_treatment: string;
  migraines: string;
  rheumatoid_arthritis: string;
  systemic_lupus: string;
  severe_mental_illness: string;
  atypical_antipsychotic: string;
  steroid_tablets: string;
  erectile_dysfunction: string;
  angina_or_heart_attack_in_family: string;
}

// Ethnicity mapping (0-9 for QRISK3)
const getEthnicityIndex = (ethnicity: string): number => {
  const ethnicityMap: { [key: string]: number } = {
    white: 1,
    indian: 2,
    pakistani: 3,
    bangladeshi: 4,
    "other-asian": 5,
    "black-caribbean": 6,
    "black-african": 7,
    chinese: 8,
    other: 9,
  };
  return ethnicityMap[ethnicity] || 1;
};

// Smoking status mapping (0-4 for QRISK3, matches qrisk.org dropdown)
const getSmokingIndex = (smokingStatus: string): number => {
  const smokingMap: { [key: string]: number } = {
    "non-smoker": 0,
    "ex-smoker": 1,
    "light-smoker": 2,
    "moderate-smoker": 3,
    "heavy-smoker": 4,
  };
  return smokingMap[smokingStatus] || 0;
};

function qrisk3BmiFromHeightWeight(heightCm: number, weightKg: number): number {
  if (heightCm <= 0) return 0;
  const heightM = heightCm / 100;
  return weightKg / (heightM * heightM);
}

// QRISK3 Female calculation - exact copy of https://qrisk.org/src.php (ClinRisk Ltd, LGPL)
const cvd_female_raw = (
  age: number,
  b_AF: number,
  b_atypicalantipsy: number,
  b_corticosteroids: number,
  b_migraine: number,
  b_ra: number,
  b_renal: number,
  b_semi: number,
  b_sle: number,
  b_treatedhyp: number,
  b_type1: number,
  b_type2: number,
  bmi: number,
  ethrisk: number,
  fh_cvd: number,
  rati: number,
  sbp: number,
  sbps5: number,
  smoke_cat: number,
  town: number
): number => {
  const survivor = 0.988876402378082;

  const Iethrisk = [
    0,
    0,
    0.28040314332995425,
    0.56298994142075398,
    0.29590000851116516,
    0.072785379877982545,
    -0.17072135508857317,
    -0.39371043314874971,
    -0.32632495283530272,
    -0.17127056883241784,
  ];

  const Ismoke = [
    0,
    0.13386833786546262,
    0.56200858012438537,
    0.66749593377502547,
    0.84948177644830847,
  ];

  const dage = age / 10;
  const age_1 = Math.pow(dage, -2) - 0.053274843841791;
  const age_2 = dage - 4.332503318786621;

  const dbmi = bmi / 10;
  const bmi_1 = Math.pow(dbmi, -2) - 0.154946178197861;
  const bmi_2 = Math.pow(dbmi, -2) * Math.log(dbmi) - 0.144462317228317;

  const rati_centered = rati - 3.47632646560669;
  const sbp_centered = sbp - 123.13001251220703;
  const sbps5_centered = sbps5 - 9.002537727355957;
  const town_centered = town - 0.392308831214905;

  let a = 0;

  a += Iethrisk[ethrisk] ?? 0;
  a += Ismoke[smoke_cat] ?? 0;

  a += age_1 * -8.1388109247726188;
  a += age_2 * 0.79733376689699098;
  a += bmi_1 * 0.29236092275460052;
  a += bmi_2 * -4.1513300213837665;
  a += rati_centered * 0.15338035820802554;
  a += sbp_centered * 0.013131488407103424;
  a += sbps5_centered * 0.0078894541014586095;
  a += town_centered * 0.077223790588590108;

  a += b_AF * 1.5923354969269663;
  a += b_atypicalantipsy * 0.25237642070115557;
  a += b_corticosteroids * 0.59520725304601851;
  a += b_migraine * 0.301267260870345;
  a += b_ra * 0.21364803435181942;
  a += b_renal * 0.65194569493845833;
  a += b_semi * 0.12555308058820178;
  a += b_sle * 0.75880938654267693;
  a += b_treatedhyp * 0.50931593683423004;
  a += b_type1 * 1.7267977510537347;
  a += b_type2 * 1.0688773244615468;
  a += fh_cvd * 0.45445319020896213;

  a += age_1 * (smoke_cat === 1 ? 1 : 0) * -4.7057161785851891;
  a += age_1 * (smoke_cat === 2 ? 1 : 0) * -2.7430383403573337;
  a += age_1 * (smoke_cat === 3 ? 1 : 0) * -0.86608088829392182;
  a += age_1 * (smoke_cat === 4 ? 1 : 0) * 0.90241562369710648;
  a += age_1 * b_AF * 19.938034889546561;
  a += age_1 * b_corticosteroids * -0.98408045235936281;
  a += age_1 * b_migraine * 1.7634979587872999;
  a += age_1 * b_renal * -3.5874047731694114;
  a += age_1 * b_sle * 19.690303738638292;
  a += age_1 * b_treatedhyp * 11.872809733921812;
  a += age_1 * b_type1 * -1.2444332714320747;
  a += age_1 * b_type2 * 6.8652342000009599;
  a += age_1 * bmi_1 * 23.802623412141742;
  a += age_1 * bmi_2 * -71.184947692087007;
  a += age_1 * fh_cvd * 0.99467807940435127;
  a += age_1 * sbp_centered * 0.034131842338615485;
  a += age_1 * town_centered * -1.0301180802035639;
  a += age_2 * (smoke_cat === 1 ? 1 : 0) * -0.075589244643193026;
  a += age_2 * (smoke_cat === 2 ? 1 : 0) * -0.11951192874867074;
  a += age_2 * (smoke_cat === 3 ? 1 : 0) * -0.10366306397571923;
  a += age_2 * (smoke_cat === 4 ? 1 : 0) * -0.13991853591718389;
  a += age_2 * b_AF * -0.076182651011162505;
  a += age_2 * b_corticosteroids * -0.12005364946742472;
  a += age_2 * b_migraine * -0.065586917898699859;
  a += age_2 * b_renal * -0.22688873086442507;
  a += age_2 * b_sle * 0.077347949679016273;
  a += age_2 * b_treatedhyp * 0.00096857823588174436;
  a += age_2 * b_type1 * -0.28724064624488949;
  a += age_2 * b_type2 * -0.097112252590695489;
  a += age_2 * bmi_1 * 0.52369958933664429;
  a += age_2 * bmi_2 * 0.045744190122323759;
  a += age_2 * fh_cvd * -0.076885051698423038;
  a += age_2 * sbp_centered * -0.0015082501423272358;
  a += age_2 * town_centered * -0.031593414674962329;

  const score = 100.0 * (1 - Math.pow(survivor, Math.exp(a)));
  return score;
};

// QRISK3 Male calculation - exact copy of https://qrisk.org/src.php (ClinRisk Ltd, LGPL)
const cvd_male_raw = (
  age: number,
  b_AF: number,
  b_atypicalantipsy: number,
  b_corticosteroids: number,
  b_impotence2: number,
  b_migraine: number,
  b_ra: number,
  b_renal: number,
  b_semi: number,
  b_sle: number,
  b_treatedhyp: number,
  b_type1: number,
  b_type2: number,
  bmi: number,
  ethrisk: number,
  fh_cvd: number,
  rati: number,
  sbp: number,
  sbps5: number,
  smoke_cat: number,
  town: number
): number => {
  const survivor = 0.977268040180206;

  const Iethrisk = [
    0,
    0,
    0.27719248760308279,
    0.47446360714931268,
    0.52961729919689371,
    0.035100159186299017,
    -0.35807899669327919,
    -0.4005648523216514,
    -0.41522792889830173,
    -0.26321348134749967,
  ];

  const Ismoke = [
    0,
    0.19128222863388983,
    0.55241588192645552,
    0.63835053027506072,
    0.78983819881858019,
  ];

  const dage = age / 10;
  const age_1 = Math.pow(dage, -1) - 0.234766781330109;
  const age_2 = Math.pow(dage, 3) - 77.284080505371094;

  const dbmi = bmi / 10;
  const bmi_1 = Math.pow(dbmi, -2) - 0.149176135659218;
  const bmi_2 = Math.pow(dbmi, -2) * Math.log(dbmi) - 0.141913309693336;

  const rati_centered = rati - 4.300998687744141;
  const sbp_centered = sbp - 128.57157897949219;
  const sbps5_centered = sbps5 - 8.756621360778809;
  const town_centered = town - 0.526304900646210;

  let a = 0;

  a += Iethrisk[ethrisk] ?? 0;
  a += Ismoke[smoke_cat] ?? 0;

  a += age_1 * -17.839781666005575;
  a += age_2 * 0.0022964880605765492;
  a += bmi_1 * 2.4562776660536358;
  a += bmi_2 * -8.3011122314711354;
  a += rati_centered * 0.17340196856327111;
  a += sbp_centered * 0.012910126542553305;
  a += sbps5_centered * 0.010251914291290456;
  a += town_centered * 0.033268201277287295;

  a += b_AF * 0.88209236928054657;
  a += b_atypicalantipsy * 0.13046879855173513;
  a += b_corticosteroids * 0.45485399750445543;
  a += b_impotence2 * 0.22251859086705383;
  a += b_migraine * 0.25584178074159913;
  a += b_ra * 0.20970658013956567;
  a += b_renal * 0.71853261288274384;
  a += b_semi * 0.12133039882047164;
  a += b_sle * 0.4401572174457522;
  a += b_treatedhyp * 0.51659871082695474;
  a += b_type1 * 1.2343425521675175;
  a += b_type2 * 0.85942071430932221;
  a += fh_cvd * 0.54055469009390156;

  a += age_1 * (smoke_cat === 1 ? 1 : 0) * -0.21011133933516346;
  a += age_1 * (smoke_cat === 2 ? 1 : 0) * 0.75268676447503191;
  a += age_1 * (smoke_cat === 3 ? 1 : 0) * 0.99315887556405791;
  a += age_1 * (smoke_cat === 4 ? 1 : 0) * 2.1331163414389076;
  a += age_1 * b_AF * 3.4896675530623207;
  a += age_1 * b_corticosteroids * 1.1708133653489108;
  a += age_1 * b_impotence2 * -1.506400985745431;
  a += age_1 * b_migraine * 2.3491159871402441;
  a += age_1 * b_renal * -0.50656716327223694;
  a += age_1 * b_treatedhyp * 6.5114581098532671;
  a += age_1 * b_type1 * 5.3379864878006531;
  a += age_1 * b_type2 * 3.6461817406221311;
  a += age_1 * bmi_1 * 31.004952956033886;
  a += age_1 * bmi_2 * -111.29157184391643;
  a += age_1 * fh_cvd * 2.7808628508531887;
  a += age_1 * sbp_centered * 0.018858524469865853;
  a += age_1 * town_centered * -0.1007554870063731;

  a += age_2 * (smoke_cat === 1 ? 1 : 0) * -0.00049854870275326121;
  a += age_2 * (smoke_cat === 2 ? 1 : 0) * -0.00079875633317385414;
  a += age_2 * (smoke_cat === 3 ? 1 : 0) * -0.00083706184266251296;
  a += age_2 * (smoke_cat === 4 ? 1 : 0) * -0.00078400319155637289;
  a += age_2 * b_AF * -0.00034995608340636049;
  a += age_2 * b_corticosteroids * -0.0002496045095297166;
  a += age_2 * b_impotence2 * -0.0011058218441227373;
  a += age_2 * b_migraine * 0.00019896446041478631;
  a += age_2 * b_renal * -0.0018325930166498813;
  a += age_2 * b_treatedhyp * 0.00063838053104165013;
  a += age_2 * b_type1 * 0.0006409780808752897;
  a += age_2 * b_type2 * -0.00024695695588868315;
  a += age_2 * bmi_1 * 0.0050380102356322029;
  a += age_2 * bmi_2 * -0.013074483002524319;
  a += age_2 * fh_cvd * -0.00024791809907396037;
  a += age_2 * sbp_centered * -0.00001271874191588457;
  a += age_2 * town_centered * -0.000093299642323272888;

  const score = 100.0 * (1 - Math.pow(survivor, Math.exp(a)));
  return score;
};

// Main QRISK3 calculation function
export const calculateQRISK3 = (data: QRISKInputData) => {
  try {
    const hasBmi = data.bmi && Number.isFinite(parseFloat(data.bmi));
    const hasHeightWeight =
      data.height &&
      data.weight &&
      Number.isFinite(parseFloat(data.height)) &&
      Number.isFinite(parseFloat(data.weight));
    if (!data.age || !data.sex || (!hasBmi && !hasHeightWeight) || !data.systolic_bp) {
      return {
        qrisk_score: 0,
        qrisk_relative_risk: 0,
        qrisk_healthy_risk: 0,
        error: "Missing required fields",
      };
    }

    // Parse numeric values; BMI from height/weight when both provided (matches qrisk.org)
    const age = parseFloat(data.age);
    const heightCm = data.height ? parseFloat(data.height) : 0;
    const weightKg = data.weight ? parseFloat(data.weight) : 0;
    let bmi: number;
    if (heightCm > 0 && weightKg > 0) {
      bmi = qrisk3BmiFromHeightWeight(heightCm, weightKg);
    } else {
      bmi = parseFloat(data.bmi);
    }
    if (!Number.isFinite(bmi) || bmi <= 0) {
      return {
        qrisk_score: 0,
        qrisk_relative_risk: 0,
        qrisk_healthy_risk: 0,
        error: "BMI or height/weight required",
      };
    }
    const sbp = parseFloat(data.systolic_bp);
    const sbps5 = parseFloat(data.systolic_bp_std_dev || "0");
    const rati = parseFloat(data.cholesterol_hdl_ratio || "4.0");
    const town = 0;

    // Validate age range
    if (age < 25 || age > 84) {
      return {
        qrisk_score: 0,
        qrisk_relative_risk: 0,
        qrisk_healthy_risk: 0,
        error: "Age must be between 25 and 84",
      };
    }

    const isMale = data.sex === "male";

    // Binary risk factors
    const b_AF = data.atrial_fibrillation === "yes" ? 1 : 0;
    const b_atypicalantipsy = data.atypical_antipsychotic === "yes" ? 1 : 0;
    const b_corticosteroids = data.steroid_tablets === "yes" ? 1 : 0;
    const b_migraine = data.migraines === "yes" ? 1 : 0;
    const b_ra = data.rheumatoid_arthritis === "yes" ? 1 : 0;
    const b_renal = data.chronic_kidney_disease === "yes" ? 1 : 0;
    const b_semi = data.severe_mental_illness === "yes" ? 1 : 0;
    const b_sle = data.systemic_lupus === "yes" ? 1 : 0;
    const b_treatedhyp = data.on_blood_pressure_treatment === "yes" ? 1 : 0;
    const b_type1 = data.diabetes_status === "type1" ? 1 : 0;
    const b_type2 = data.diabetes_status === "type2" ? 1 : 0;
    const b_impotence2 = data.erectile_dysfunction === "yes" ? 1 : 0;
    const fh_cvd =
      data.angina_or_heart_attack_in_family === "yes" ||
      data.family_history_cvd === "yes"
        ? 1
        : 0;

    // Ethnicity and smoking categories
    const ethrisk = getEthnicityIndex(data.ethnicity);
    const smoke_cat = getSmokingIndex(data.smoking_status);

    let qrisk_score: number;

    if (isMale) {
      qrisk_score = cvd_male_raw(
        age,
        b_AF,
        b_atypicalantipsy,
        b_corticosteroids,
        b_impotence2,
        b_migraine,
        b_ra,
        b_renal,
        b_semi,
        b_sle,
        b_treatedhyp,
        b_type1,
        b_type2,
        bmi,
        ethrisk,
        fh_cvd,
        rati,
        sbp,
        sbps5,
        smoke_cat,
        town
      );
    } else {
      qrisk_score = cvd_female_raw(
        age,
        b_AF,
        b_atypicalantipsy,
        b_corticosteroids,
        b_migraine,
        b_ra,
        b_renal,
        b_semi,
        b_sle,
        b_treatedhyp,
        b_type1,
        b_type2,
        bmi,
        ethrisk,
        fh_cvd,
        rati,
        sbp,
        sbps5,
        smoke_cat,
        town
      );
    }

    // Calculate healthy person risk (same age/sex, no risk factors, healthy BMI/BP)
    let qrisk_healthy_risk: number;
    if (isMale) {
      qrisk_healthy_risk = cvd_male_raw(
        age,
        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
        25, // healthy BMI
        1,  // white ethnicity (baseline)
        0,
        4.0, // normal cholesterol ratio
        120, // normal SBP
        0,
        0,  // non-smoker
        0   // average Townsend
      );
    } else {
      qrisk_healthy_risk = cvd_female_raw(
        age,
        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
        25, // healthy BMI
        1,  // white ethnicity (baseline)
        0,
        3.5, // normal cholesterol ratio
        120, // normal SBP
        0,
        0,  // non-smoker
        0   // average Townsend
      );
    }

    // Calculate relative risk
    const qrisk_relative_risk =
      qrisk_healthy_risk > 0 ? qrisk_score / qrisk_healthy_risk : 1;

    return {
      qrisk_score: Math.round(qrisk_score * 10) / 10,
      qrisk_relative_risk: Math.round(qrisk_relative_risk * 10) / 10,
      qrisk_healthy_risk: Math.round(qrisk_healthy_risk * 10) / 10,
    };
  } catch (error) {
    console.error("Error calculating QRISK3:", error);
    return {
      qrisk_score: 0,
      qrisk_relative_risk: 0,
      qrisk_healthy_risk: 0,
      error: "Calculation error",
    };
  }
};
